// Auth gate — shown on first load when no token is stored.
// The access key IS the MCP_AUTH_TOKEN wrangler secret.

import { AUTH_TOKEN_KEY } from './worker';

const WORKER = 'https://twilight-dew-3ae7.delfrost42.workers.dev';

async function testToken(token: string): Promise<boolean> {
  try {
    const res = await fetch(`${WORKER}/api/auth-check`, {
      method: 'POST',
      body: '{}',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      signal: AbortSignal.timeout(6000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchAndApplySettings(token: string): Promise<void> {
  try {
    const res = await fetch(`${WORKER}/api/settings`, {
      headers: { 'Authorization': `Bearer ${token}` },
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return;
    const data = await res.json() as Record<string, string>;
    for (const [k, v] of Object.entries(data)) {
      if (v) localStorage.setItem(k, v);
    }
  } catch {
    // silent — app works fine with just localStorage
  }
}

export async function requireAuth(): Promise<void> {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    await fetchAndApplySettings(token);
    return;
  }

  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.id = 'auth-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:var(--bg);z-index:99999;display:flex;align-items:center;justify-content:center;font-family:var(--mono);';

    overlay.innerHTML = `
      <div style="background:var(--panel);border:1px solid var(--border);border-radius:6px;padding:44px 40px;width:100%;max-width:380px;text-align:center;">
        <div style="font-family:var(--sans);font-size:20px;font-weight:900;color:var(--text);letter-spacing:0.12em;margin-bottom:4px;">MERIDIAN</div>
        <div style="font-size:10px;color:var(--text3);letter-spacing:0.2em;margin-bottom:36px;">TERMINAL v6.0</div>
        <div style="font-size:10px;color:var(--text3);letter-spacing:0.15em;text-transform:uppercase;margin-bottom:12px;">Access Key</div>
        <input id="auth-input" type="password" autocomplete="off" spellcheck="false"
          style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);font-family:var(--mono);font-size:14px;padding:12px 16px;border-radius:3px;outline:none;box-sizing:border-box;text-align:center;letter-spacing:0.08em;">
        <div id="auth-err" style="min-height:18px;margin-top:10px;font-size:11px;color:var(--red);letter-spacing:0.05em;"></div>
        <button id="auth-btn"
          style="width:100%;margin-top:6px;background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.3);color:var(--accent);font-family:var(--mono);font-size:11px;font-weight:700;letter-spacing:0.15em;padding:12px;cursor:pointer;border-radius:3px;">
          UNLOCK
        </button>
      </div>`;

    document.body.appendChild(overlay);

    const input  = overlay.querySelector<HTMLInputElement>('#auth-input')!;
    const btn    = overlay.querySelector<HTMLButtonElement>('#auth-btn')!;
    const errEl  = overlay.querySelector<HTMLElement>('#auth-err')!;

    setTimeout(() => input.focus(), 80);

    async function submit() {
      const token = input.value.trim();
      if (!token) return;
      btn.disabled = true;
      btn.textContent = 'VERIFYING…';
      errEl.textContent = '';

      const ok = await testToken(token);
      if (ok) {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        await fetchAndApplySettings(token);
        overlay.remove();
        resolve();
      } else {
        errEl.textContent = '✗  Invalid key';
        btn.disabled = false;
        btn.textContent = 'UNLOCK';
        input.select();
      }
    }

    btn.addEventListener('click', submit);
    input.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') submit(); });
  });
}
